import PropTypes from 'prop-types';
import React from 'react';

import Chart from '../../../models/chart';

import { WeezHeader, Button, Icon, Card, EmptyState, FilterSetController, CheckboxLabel } from '@weezevent/nacre';

import { Trans, useTranslation } from 'react-i18next';
import ChartDrawer from '../chartDrawer';
import ChartCard from './chartList';
import DeleteModal from './deleteModal';
import DuplicateModal from './duplicateModal';
import LoadingCard from '../../../components/loadingCard';
import Auth from '../../../utils/auth';
import Style from './chartList.module.css';
import { usePermissions } from '../../../user/permissions';

function getDuplicateFormInitialValues(chart) {
    return chart
        ? {
              name: `${chart?.name} - copy`,
              organization: chart?.organization
          }
        : {};
}

export default function ChartList({ organization }) {
    const { t } = useTranslation();
    const permissions = usePermissions();

    const [showDrawer, setShowDrawer] = React.useState(false);
    const [chartInDrawer, setChartInDrawer] = React.useState(null);
    const [chartDeleted, setChartDeleted] = React.useState(null);
    const [inputSearchValue, setInputSearchValue] = React.useState('');
    const [filterOnlySeated, setFilterOnlySeated] = React.useState(true);

    const [chartToDuplicate, setChartToDuplicate] = React.useState(null);

    const [charts, loadingCharts, syncCharts] = Chart.useApiModel(
        {
            organizationId: organization.id,
            query: {
                only_seated: filterOnlySeated,
                search: inputSearchValue
            }
        },
        [organization, inputSearchValue, filterOnlySeated]
    );

    const onCloseDeleteModal = React.useCallback(() => {
        setChartDeleted(null);
    }, []);

    const handleSearch = React.useCallback((synth, value) => {
        setInputSearchValue(value);
    }, []);

    const chartsCount = React.useMemo(() => {
        return charts?.length;
    }, [charts]);

    const handleDuplicateModalOpen = React.useCallback(chart => {
        setChartToDuplicate(chart);
    }, []);

    const handleDuplicateModalClose = React.useCallback(() => {
        setChartToDuplicate(null);
    }, []);

    const handleDuplicateModalSubmit = React.useCallback(
        ({ values }) =>
            chartToDuplicate
                .duplicate(values, {
                    id: chartToDuplicate.pk,
                    organizationId: organization.id
                })
                .then(result => {
                    const chart = result?.response;
                    if (!chart) {
                        // Refresh the list. Should never happen normally
                        setChartToDuplicate(null);
                        syncCharts({ allow_cache: false, cache: false });
                    } else {
                        // We use window.location.href instead of history.push to force a full reload of the page
                        // to avoid issues with charts being cached in various places
                        // Also it's necessary to properly initialize the chart dashboard page for another organization
                        // ChartCard do the same on click (Uses a tag with href attribute)
                        window.location.href = `/ticket/O${chart.organization}/charts/${chart.uuid}/dashboard`;
                    }
                }),
        [chartToDuplicate]
    );

    return (
        <React.Fragment>
            <ChartDrawer
                chart={chartInDrawer}
                open={showDrawer}
                organization={organization}
                onClickAway={() => {
                    setShowDrawer(false);
                    setChartInDrawer(null);
                }}
                onSubmit={() => {
                    setShowDrawer(false);
                    setChartInDrawer(null);
                    syncCharts();
                }}
            />
            <WeezHeader
                title={<Trans i18nKey="ticket.charts.chartlist.title">Liste des plans</Trans>}
                rightComponent={<Button primary onClick={() => setShowDrawer(true)} label={<Trans i18nKey="ticket.charts.chartlist.addChart" />} />}
            />
            <Card>
                <Card.Content>
                    <FilterSetController
                        labelTotal={t('ticket.charts.chartlist.chart', { count: chartsCount })}
                        onChange={() => {}}
                        search={{
                            onChange: (synth, value) => {
                                handleSearch(synth, value);
                            },
                            handleReset: () => {
                                handleSearch('');
                            },
                            placeholder: t('ticket.charts.chartlist.searchChart'),
                            value: inputSearchValue
                        }}
                        total={chartsCount}
                        isLoadingTotal={loadingCharts}
                    />
                    <Auth allow={['RoleCapacity.ADMIN', 'PyvarCapacity.ADMIN']}>
                        <CheckboxLabel
                            className={Style['filter_only_seated']}
                            label={t('ticket.charts.chartlist.filters.onlySeated')}
                            checked={filterOnlySeated}
                            onChange={(e, data) => {
                                setFilterOnlySeated(data.checked);
                            }}
                        />
                    </Auth>
                </Card.Content>
            </Card>
            <LoadingCard loading={loadingCharts} height="50vh">
                {charts?.length > 0 ? (
                    <>
                        <DeleteModal chart={chartDeleted} syncCharts={syncCharts} organization={organization} onClose={onCloseDeleteModal} />
                        <DuplicateModal
                            isOpen={Boolean(chartToDuplicate)}
                            onClose={handleDuplicateModalClose}
                            onSubmit={handleDuplicateModalSubmit}
                            initialFormValues={getDuplicateFormInitialValues(chartToDuplicate)}
                        />
                        {charts.map((chart, index) => (
                            <ChartCard
                                key={index}
                                organization={organization}
                                chart={chart}
                                setChartInDrawer={setChartInDrawer}
                                setShowDrawer={setShowDrawer}
                                onChartDeleted={setChartDeleted}
                                onChartDuplicated={
                                    permissions.hasCapacities(['PyvarCapacity.CHART', 'PyvarCapacity.CHART_ADMIN'], organization?.id) ? handleDuplicateModalOpen : null
                                }
                            />
                        ))}
                    </>
                ) : (
                    <Card>
                        <Card.Content>
                            <EmptyState
                                icon={<Icon name="list" />}
                                title={inputSearchValue ? t('ticket.charts.chartlist.empty-search') : t('ticket.charts.chartlist.empty')}
                                labelButton={inputSearchValue ? '' : t('ticket.charts.chartlist.addChart')}
                                onClick={() => setShowDrawer(true)}
                            />
                        </Card.Content>
                    </Card>
                )}
            </LoadingCard>
        </React.Fragment>
    );
}

ChartList.propTypes = {
    organization: PropTypes.object.isRequired
};
